/**
 * Compose step for emailing a shared item to the people who already have
 * access (#10815 for meeting notes, then every share modal).
 *
 * Modelled on Google Drive's Share modal: the email action is shorthand for
 * "tell the people who can already open this", so the recipient list is the
 * item's access list rather than a free-text address field. Nobody who lacks
 * access can be picked, and the backend re-checks anyway. `noun` is what the
 * copy calls the item ("note", "chat", "report", ...).
 */

import React, { useMemo, useState } from "react";
import {
  CheckSquare,
  Square,
  PaperPlaneTilt,
  ArrowLeft,
} from "@phosphor-icons/react";

export interface EmailRecipient {
  id: string;
  name: string;
  email?: string;
}

interface EmailNotePanelProps {
  /** What the copy calls the item: "note", "chat", "report", ... */
  noun: string;
  /**
   * Whether access reaches the whole organization (org sharing, link sharing
   * or inherited org access). Those members are not offered as recipients,
   * so with no one added by name the empty state must say that rather than
   * claim nobody has access.
   */
  sharedWithEveryone: boolean;
  recipients: EmailRecipient[];
  onBack: () => void;
  onSend: (recipientIds: string[], message: string) => void;
  isSending: boolean;
  errorMessage: string | null;
  /** Set once a send has succeeded; renders the inline confirmation. */
  sentCount: number | null;
}

const SELECTED_TEXT = "#2A3DCB";
const SELECTED_BG = "#F7F8FF";

const EmailNotePanel: React.FC<EmailNotePanelProps> = ({
  noun,
  sharedWithEveryone,
  recipients,
  onBack,
  onSend,
  isSending,
  errorMessage,
  sentCount,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(recipients.map((r) => r.id))
  );
  const [message, setMessage] = useState("");

  const selectedCount = useMemo(
    () => recipients.filter((r) => selectedIds.has(r.id)).length,
    [recipients, selectedIds]
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (sentCount !== null) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          {sentCount === 1
            ? `Emailed the ${noun} to 1 person.`
            : `Emailed the ${noun} to ${sentCount} people.`}
        </p>
        <div className="flex justify-start pt-2">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-700 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={16} weight="regular" className="mr-2" />
            <span className="text-sm">Back to sharing</span>
          </button>
        </div>
      </div>
    );
  }

  if (recipients.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          {sharedWithEveryone
            ? `Everyone in the organization can open this ${noun}. Only people added by name can be emailed, and no one has been added yet.`
            : `No one else has access to this ${noun} yet. Go back and share it with someone, then you can email it to them.`}
        </p>
        <div className="flex justify-start pt-2">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-700 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={16} weight="regular" className="mr-2" />
            <span className="text-sm">Back to sharing</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Email a link to this {noun}. Only people who already have access can be
        picked.
      </p>

      <div className="max-h-48 overflow-y-auto">
        {recipients.map((recipient) => {
          const checked = selectedIds.has(recipient.id);
          return (
            <button
              key={recipient.id}
              onClick={() => toggle(recipient.id)}
              aria-pressed={checked}
              className="flex items-center gap-3 w-full text-left py-2 px-2 rounded-lg transition-colors"
              style={checked ? { backgroundColor: SELECTED_BG } : undefined}
            >
              {checked ? (
                <CheckSquare size={18} weight="fill" color={SELECTED_TEXT} />
              ) : (
                <Square size={18} weight="regular" className="text-gray-400" />
              )}
              <span className="min-w-0">
                <span
                  className="block text-sm truncate"
                  style={checked ? { color: SELECTED_TEXT } : undefined}
                >
                  {recipient.name}
                </span>
                {recipient.email && (
                  <span className="block text-xs text-gray-500 truncate">
                    {recipient.email}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Add a message (optional)"
        rows={3}
        className="w-full text-sm text-gray-800 border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:border-gray-400"
      />

      {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}

      <div className="flex justify-between pt-2">
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 text-gray-700 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={16} weight="regular" className="mr-2" />
          <span className="text-sm">Back</span>
        </button>
        <button
          onClick={() =>
            onSend(
              recipients.filter((r) => selectedIds.has(r.id)).map((r) => r.id),
              message
            )
          }
          disabled={selectedCount === 0 || isSending}
          className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PaperPlaneTilt size={16} weight="regular" className="mr-2" />
          <span className="text-sm">
            {isSending ? "Sending…" : `Send to ${selectedCount}`}
          </span>
        </button>
      </div>
    </div>
  );
};

export default EmailNotePanel;
